// School seats - client side.
//
// Backed by public.schools / school_admins / school_seats (migration
// 0004_school_seats.sql). A pupil's seat is claimed automatically on sign-in via
// the claim_school_seats() RPC, which matches on their verified email. SENCO
// actions go through /api/school/* with the user's access token.

import { authHeaders, supabase } from './supabase';

export interface SchoolSeat {
  seat_id: string;
  school_id: string;
  school_name: string;
  status: 'pending' | 'active' | 'revoked';
  granted_at: string | null;
  expires_at: string | null;
  seen_at: string | null;
}

/** Link any seats invited to this user's email and return their current active seat. */
export async function claimSchoolSeat(): Promise<SchoolSeat | null> {
  const { data, error } = await supabase.rpc('claim_school_seats');
  if (error || !Array.isArray(data) || !data.length) return null;
  return data[0] as SchoolSeat;
}

export function isActiveSchoolSeat(seat: SchoolSeat | null): boolean {
  if (!seat || seat.status !== 'active') return false;
  if (seat.expires_at && new Date(seat.expires_at).getTime() < Date.now()) return false;
  return true;
}

export async function markSchoolSeatSeen(seatId: string): Promise<void> {
  await supabase.rpc('mark_school_seat_seen', { p_seat_id: seatId });
}

/** True if this signed-in user manages at least one school. */
export async function fetchIsSchoolAdmin(): Promise<boolean> {
  const { data, error } = await supabase.from('school_admins').select('school_id').limit(1);
  if (error) return false;
  return (data ?? []).length > 0;
}

// -----------------------------------------------------------------------------
// SENCO dashboard API
// -----------------------------------------------------------------------------

export interface SchoolSeatRow {
  id: string;
  pupilEmail: string;
  pupilLabel: string | null;
  status: 'pending' | 'active' | 'revoked';
  invitedAt: string;
  grantedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  worksheetsUsed: number;
}

export interface SchoolUsage {
  school: { id: string; name: string; seatLimit: number; licenceExpiresAt: string | null };
  counts: { active: number; pending: number; available: number; worksheets: number };
  seats: SchoolSeatRow[];
}

export interface InviteResult {
  invited: number;
  alreadySeated: string[];
  overLimit: string[];
  invalid: string[];
  emailFailed: boolean;
}

async function schoolApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/school/${path}`, { ...init, headers: await authHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as { error?: string }).error || `Request failed (${res.status})`);
  return body as T;
}

export function fetchSchoolUsage(): Promise<SchoolUsage> {
  return schoolApi<SchoolUsage>('usage');
}

export function invitePupils(pupils: { email: string; label?: string }[]): Promise<InviteResult> {
  return schoolApi<InviteResult>('invite', { method: 'POST', body: JSON.stringify({ pupils }) });
}

export function revokeSeats(seatIds: string[]): Promise<{ revoked: number }> {
  return schoolApi<{ revoked: number }>('revoke', { method: 'POST', body: JSON.stringify({ seatIds }) });
}

/**
 * Parse pasted text or a CSV export into pupils. Accepts one pupil per line,
 * "email" or "email, label" or "label, email" in any column order - the email is
 * whichever cell looks like one, the first other non-empty cell becomes the label.
 */
export function parsePupilList(text: string): { email: string; label?: string }[] {
  const out: { email: string; label?: string }[] = [];
  for (const line of text.split(/\r?\n/)) {
    const cells = line.split(/[,;\t]/).map((c) => c.trim().replace(/^"|"$/g, '')).filter(Boolean);
    const email = cells.find((c) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c));
    if (!email) continue;
    const label = cells.find((c) => c !== email);
    out.push({ email: email.toLowerCase(), ...(label ? { label } : {}) });
  }
  return out;
}
